import { Box, VStack } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';

import { AiSearchTrackingEvents } from '@revelio/core';

import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import { useFilterContext } from '../../contexts/FilterProvider';
import { getFilterDefinition } from '../../engine/filterRegistry';
import type { MultiInstanceMeta } from '../../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  isSegmentedFilter,
  type TreeFilter,
} from '../../types/filters';
import { createSegmentedFilter, isDiscoveryPage } from '../../utils/common';
import {
  canFilterBeSegmented,
  isSegmentableFilterType,
} from '../../utils/filterSegmentation';
import DateFilterForm from '../filterForms/DateFilterForm';
import { DateRangeFilterForm } from '../filterForms/DateRangeFilterForm';
import { FreeTextFilterForm } from '../filterForms/FreeTextFilterForm';
import { ListFilterForm } from '../filterForms/ListFilterForm';
import { NotToggle } from '../filterForms/NotToggle';
import { SegmentedRangeFilterForm } from '../filterForms/SegmentedRangeFilterForm';
import { TreeFilterForm } from '../filterForms/TreeFilterForm';
import { NameFilterForm } from '../filterForms/talent-discovery/NameFilterForm';
import { SchoolSearchFilterForm } from '../filterForms/talent-discovery/SchoolSearchFilterForm';
import { StaticListFilterForm } from '../filterForms/talent-discovery/StaticListFilterForm';
import { SegmentForm } from '../segments/SegmentForm';
import {
  mapSegmentOptionToKey,
  type Segment,
  SegmentSelector,
} from '../segments/SegmentSelector';
import { useSegmentFlow } from '../segments/useSegmentFlow';

type SegmentedFilterFormRenderProps = {
  filter: Filter | null;
  close: () => void;
  selectedMultiMeta?: MultiInstanceMeta;
  selectedFilterId?: FilterId | 'AI_SEARCH' | null;
  segmentStep: number;
  setSegmentStep: (step: number) => void;
  setSelectedSegmentKey?: (key: string | null) => void;
  handleSegmentBack: () => void;
};

export const SegmentedFilterFormRender = ({
  filter,
  close,
  selectedMultiMeta,
  selectedFilterId: _selectedFilterId,
  segmentStep: externalSegmentStep,
  setSegmentStep: setExternalSegmentStep,
  setSelectedSegmentKey: setExternalSelectedSegmentKey,
  handleSegmentBack: externalHandleSegmentBack,
}: SegmentedFilterFormRenderProps) => {
  const { dispatch, activeFilterMap } = useFilterContext();
  const { revelioPageId } = useTabContentConfig();

  // Get the current filter from state to ensure we have the latest segments
  const currentFilterFromState = filter?.id
    ? activeFilterMap.get(filter.id)
    : null;
  // Prefer the original filter if it's a SegmentedFilter (has segmentations),
  // otherwise use the filter from state (which may have more recent updates)
  const filterToUse =
    filter && isSegmentedFilter(filter)
      ? filter
      : currentFilterFromState || filter;

  const {
    selectedSegmentKey: internalSelectedSegmentKey,
    localSegmentations,
    setLocalSegmentations,
    setSelectedSegmentKey: setInternalSelectedSegmentKey,
    setPendingSegmentFilter,
    pendingSegmentFilter,
    handleNewSegment: internalHandleNewSegment,
    handleSegmentSelect: internalHandleSegmentSelect,
    handleEditSegment: internalHandleEditSegment,
    handleRemoveSegment,
    canBeSegmented,
    segmentations,
    getUnwrappedFilter,
  } = useSegmentFlow(filterToUse, externalSegmentStep, setExternalSegmentStep);

  const selectedSegmentKey = internalSelectedSegmentKey;
  const setSelectedSegmentKey = setInternalSelectedSegmentKey;

  // Wrap handleEditSegment to sync with external state
  const handleEditSegment = (segmentKey: string) => () => {
    internalHandleEditSegment(segmentKey)();
    setExternalSelectedSegmentKey?.(segmentKey);
    setExternalSegmentStep(2);
  };

  // Wrap handlers to sync with external state
  const handleSegmentSelect = (segment: Segment) => {
    internalHandleSegmentSelect(segment);
    const segmentKey = mapSegmentOptionToKey(segment);
    if (segmentKey) {
      setExternalSelectedSegmentKey?.(segmentKey);
      setExternalSegmentStep(2);
    }
  };

  const handleNewSegment = () => {
    internalHandleNewSegment();
    setExternalSegmentStep(1);
  };

  const handleSegmentBack = externalHandleSegmentBack;

  const segmentStep = externalSegmentStep;

  const unwrappedFilter = getUnwrappedFilter();

  // Use pendingSegmentFilter when on step 1 to pre-render step 2 form
  const filterForStep2 = (() => {
    if (segmentStep === 2) {
      return unwrappedFilter;
    }
    if (segmentStep === 1 && pendingSegmentFilter) {
      return pendingSegmentFilter;
    }
    return null;
  })();

  // Centralized isNot state management for filters that support it
  const supportsIsNot =
    unwrappedFilter &&
    (unwrappedFilter.type === FilterType.LIST ||
      unwrappedFilter.type === FilterType.STATIC_LIST ||
      unwrappedFilter.type === FilterType.TREE ||
      unwrappedFilter.type === FilterType.RANGE ||
      unwrappedFilter.type === FilterType.FREE_TEXT ||
      unwrappedFilter.type === FilterType.SCHOOL);

  const showNotToggle: boolean =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY && !!supportsIsNot;

  const [isNotEnabled, setIsNotEnabled] = useState<boolean>(
    unwrappedFilter && 'isNot' in unwrappedFilter
      ? (unwrappedFilter.isNot ?? false)
      : false
  );

  // Determine which filter to use for step 2 forms (pre-render when on step 1)
  const treeFilterForStep2: TreeFilter | null =
    filterForStep2?.type === FilterType.TREE
      ? (filterForStep2 as TreeFilter)
      : null;

  // For step 0 (main filter form), use unwrappedFilter if it's a tree filter
  const treeFilterForForm: TreeFilter | null =
    segmentStep === 0 && unwrappedFilter?.type === FilterType.TREE
      ? unwrappedFilter
      : null;

  const freeTextFilterForStep2: FreeTextFilter | null =
    filterForStep2?.type === FilterType.FREE_TEXT &&
    filterForStep2.id !== FilterId.NAME
      ? (filterForStep2 as FreeTextFilter)
      : null;

  const freeTextFilterForForm: FreeTextFilter | null =
    segmentStep !== 2 &&
    unwrappedFilter?.type === FilterType.FREE_TEXT &&
    unwrappedFilter.id !== FilterId.NAME
      ? unwrappedFilter
      : null;

  const handleSaveFilter = (updatedFilter: Filter) => {
    const isNotValue = (() => {
      if (updatedFilter.type === FilterType.RANGE) {
        return 'isNot' in updatedFilter
          ? (updatedFilter.isNot ?? false)
          : false;
      }

      if (!supportsIsNot) {
        return undefined;
      }

      if ('isNot' in updatedFilter && updatedFilter.isNot !== undefined) {
        return updatedFilter.isNot;
      }

      return isNotEnabled;
    })();

    const nextFilter: Filter = {
      ...updatedFilter,
      isUserSubmitted: true,
      ...(isNotValue !== undefined && { isNot: isNotValue }),
    };

    // Same event shape as FilterFormRender.handleSaveFilter — segmented forms
    // are the other manual filter-submit choke-point, so both must fire for the
    // AI-vs-manual split to count all manual applies.
    const selectionCount =
      'selections' in nextFilter && Array.isArray(nextFilter.selections)
        ? nextFilter.selections.length
        : undefined;
    mixpanel.track(AiSearchTrackingEvents.FILTERS_APPLIED, {
      method: 'manual',
      filter_id: nextFilter.id,
      filter_type: nextFilter.type,
      is_multi_instance: Boolean(selectedMultiMeta),
      ...(isNotValue !== undefined && { is_not: isNotValue }),
      ...(selectionCount !== undefined && { selection_count: selectionCount }),
      page_id: revelioPageId,
    });

    if (selectedMultiMeta) {
      // If editing a segment (step 2) within a multi-filter context, save to local state
      if (segmentStep === 2 && selectedSegmentKey && filterToUse) {
        setLocalSegmentations((prev) => ({
          ...(prev || {}),
          [selectedSegmentKey]: nextFilter,
        }));
        setPendingSegmentFilter(null);
        setSelectedSegmentKey(null);
        setExternalSegmentStep(0);
        return;
      }

      // When adding/updating a multi-instance, check if parent expects segmented instances
      const parentDef = getFilterDefinition(
        selectedMultiMeta.parentMultiFilterId
      );
      const wrappedDef =
        parentDef?.type === FilterType.MULTI && parentDef.wrappedFilterId
          ? getFilterDefinition(parentDef.wrappedFilterId)
          : null;
      const shouldWrapAsSegmented =
        wrappedDef &&
        canFilterBeSegmented(wrappedDef) &&
        isSegmentableFilterType(nextFilter);

      const instanceToAdd = shouldWrapAsSegmented
        ? createSegmentedFilter({
            wrappedFilter: nextFilter,
            baseProps: nextFilter,
            segmentations: localSegmentations ?? {},
          })
        : nextFilter;

      if (selectedMultiMeta.isNewMultiInstance) {
        dispatch({
          type: 'ADD_MULTI_FILTER_INSTANCE',
          payload: {
            parentId: selectedMultiMeta.parentMultiFilterId,
            newInstance: instanceToAdd,
          },
        });
      } else if (selectedMultiMeta.instanceIndex !== undefined) {
        dispatch({
          type: 'UPDATE_MULTI_FILTER_INSTANCE',
          payload: {
            parentId: selectedMultiMeta.parentMultiFilterId,
            instanceIndex: selectedMultiMeta.instanceIndex,
            newInstance: instanceToAdd,
          },
        });
      }
      return;
    }

    // If editing a segment (step 2), update segment immediately
    if (segmentStep === 2 && selectedSegmentKey && filterToUse) {
      setLocalSegmentations((prev) => ({
        ...(prev || {}),
        [selectedSegmentKey]: nextFilter,
      }));
      dispatch({
        type: 'UPDATE_SEGMENT_IN_FILTER',
        payload: {
          filterId: filterToUse.id,
          segmentKey: selectedSegmentKey,
          segmentFilter: nextFilter,
        },
      });
      setPendingSegmentFilter(null);
      setSelectedSegmentKey(null);
      setExternalSegmentStep(0);
      return;
    }

    // When saving the main filter, include segments from local component state
    if (canFilterBeSegmented(nextFilter) && localSegmentations !== null) {
      dispatch({
        type: 'UPDATE_SEGMENTED_FILTER',
        payload: {
          filterId: nextFilter.id,
          wrappedFilter: nextFilter,
          segmentations: localSegmentations,
        },
      });
    } else {
      // Not segmentable or no local segments: save normally
      if (filterToUse && isSegmentedFilter(filterToUse)) {
        dispatch({
          type: 'UPDATE_SEGMENTED_FILTER',
          payload: {
            filterId: filterToUse.id,
            wrappedFilter: nextFilter,
          },
        });
      } else {
        dispatch({ type: 'SET_FILTER', filter: nextFilter });
      }
    }
  };

  // Helper to wrap form with NotToggle if needed
  const renderFormWithNotToggle = (
    form: React.ReactNode,
    filterType: FilterType
  ) => {
    // ListFilterForm renders NotToggle inside CustomMenuList, so don't wrap it
    if (filterType === FilterType.LIST) {
      return form;
    }

    // TreeFilterForm handles NotToggle internally via middleContent
    if (filterType === FilterType.TREE && showNotToggle) {
      return form;
    }

    // FreeTextFilterForm handles NotToggle inline
    if (filterType === FilterType.FREE_TEXT && showNotToggle) {
      return form;
    }

    if (showNotToggle && unwrappedFilter) {
      return (
        <VStack spacing={2} align="stretch">
          <NotToggle
            filterName={unwrappedFilter.name}
            isNot={isNotEnabled}
            onChange={setIsNotEnabled}
            pl={0}
          />
          {form}
        </VStack>
      );
    }

    return form;
  };

  const handleCloseForStep =
    segmentStep === 2 ? () => setExternalSegmentStep(0) : close;

  return (
    <>
      {segmentStep >= 1 && (
        <Box display={segmentStep === 1 ? 'block' : 'none'}>
          <SegmentSelector
            onSegmentSelect={handleSegmentSelect}
            onClickBack={handleSegmentBack}
          />
        </Box>
      )}

      {canBeSegmented && segmentStep === 0 && (
        <SegmentForm
          segmentations={segmentations}
          onEditSegment={handleEditSegment}
          onRemoveSegment={handleRemoveSegment}
          onNewSegment={handleNewSegment}
        />
      )}
      {unwrappedFilter?.id === FilterId.SNAPSHOT_DATE && segmentStep !== 1 && (
        <DateFilterForm closeMenu={handleCloseForStep} />
      )}
      {unwrappedFilter?.id === FilterId.DATE_RANGE && segmentStep !== 1 && (
        <DateRangeFilterForm closeMenu={handleCloseForStep} />
      )}
      {unwrappedFilter?.type === FilterType.SCHOOL &&
        segmentStep !== 1 &&
        renderFormWithNotToggle(
          <SchoolSearchFilterForm
            filter={unwrappedFilter}
            onClose={handleCloseForStep}
            onSave={handleSaveFilter}
            isNewInstance={selectedMultiMeta?.isNewMultiInstance}
          />,
          FilterType.SCHOOL
        )}
      {treeFilterForStep2 && (
        <Box display={segmentStep === 2 ? 'block' : 'none'}>
          {renderFormWithNotToggle(
            <TreeFilterForm
              filter={treeFilterForStep2}
              disallowMultiLevelSelect={!isDiscoveryPage(revelioPageId)}
              closeMenu={handleCloseForStep}
              onSave={handleSaveFilter}
              isNewInstance={selectedMultiMeta?.isNewMultiInstance}
              isNotEnabled={isNotEnabled}
              onIsNotEnabledChange={setIsNotEnabled}
              showNotToggle={showNotToggle}
            />,
            FilterType.TREE
          )}
        </Box>
      )}
      {treeFilterForForm && segmentStep === 0 && (
        <Box display={segmentStep === 0 ? 'block' : 'none'}>
          {renderFormWithNotToggle(
            <TreeFilterForm
              filter={treeFilterForForm}
              disallowMultiLevelSelect={!isDiscoveryPage(revelioPageId)}
              closeMenu={handleCloseForStep}
              onSave={handleSaveFilter}
              isNewInstance={selectedMultiMeta?.isNewMultiInstance}
              isNotEnabled={isNotEnabled}
              onIsNotEnabledChange={setIsNotEnabled}
              showNotToggle={showNotToggle}
            />,
            FilterType.TREE
          )}
        </Box>
      )}
      {filterForStep2?.type === FilterType.LIST && (
        <Box display={segmentStep === 2 ? 'block' : 'none'}>
          <ListFilterForm
            filter={filterForStep2}
            closeMenu={handleCloseForStep}
            onSave={handleSaveFilter}
            isNotEnabled={isNotEnabled}
            onIsNotEnabledChange={setIsNotEnabled}
            showNotToggle={showNotToggle}
          />
        </Box>
      )}
      {filterForStep2?.type === FilterType.STATIC_LIST && (
        <Box display={segmentStep === 2 ? 'block' : 'none'}>
          <StaticListFilterForm
            filter={filterForStep2}
            closeMenu={handleCloseForStep}
            onSave={handleSaveFilter}
            isNotEnabled={isNotEnabled}
            onIsNotEnabledChange={setIsNotEnabled}
            showNotToggle={showNotToggle}
          />
        </Box>
      )}
      {unwrappedFilter?.type === FilterType.FREE_TEXT &&
        unwrappedFilter?.id === FilterId.NAME &&
        segmentStep !== 1 && (
          <NameFilterForm
            filter={unwrappedFilter}
            closeMenu={handleCloseForStep}
          />
        )}
      {freeTextFilterForStep2 && (
        <Box display={segmentStep === 2 ? 'block' : 'none'}>
          <FreeTextFilterForm
            filter={freeTextFilterForStep2}
            closeMenu={handleCloseForStep}
            onSave={handleSaveFilter}
            isNewInstance={selectedMultiMeta?.isNewMultiInstance}
            isNotEnabled={isNotEnabled}
            onIsNotEnabledChange={setIsNotEnabled}
            showNotToggle={showNotToggle}
          />
        </Box>
      )}
      {freeTextFilterForForm && segmentStep !== 1 && (
        <Box display={segmentStep === 0 ? 'block' : 'none'}>
          <FreeTextFilterForm
            filter={freeTextFilterForForm}
            closeMenu={handleCloseForStep}
            onSave={handleSaveFilter}
            isNewInstance={selectedMultiMeta?.isNewMultiInstance}
            isNotEnabled={isNotEnabled}
            onIsNotEnabledChange={setIsNotEnabled}
            showNotToggle={showNotToggle}
          />
        </Box>
      )}
      {unwrappedFilter?.type === FilterType.RANGE && segmentStep !== 1 && (
        <SegmentedRangeFilterForm
          filter={unwrappedFilter}
          closeMenu={handleCloseForStep}
          onSave={handleSaveFilter}
        />
      )}
    </>
  );
};
